import { getSettingOwFOpeningYearOptions } from "@faire/retailer-visitor-shared/settings/getSettingOwFOpeningYearOptions";
import {
  DEPRECATED_backwardsCompatibleValues,
  StrictLocalizeFunction,
} from "@faire/web/common/localization/localize";
import { isNotNull } from "@faire/web/common/typescriptUtils";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { getYear } from "date-fns/getYear";
import { subYears } from "date-fns/subYears";

const CREATION_TIME_LABEL = (
  strictLocalize: StrictLocalizeFunction
): {
  [K in IRetailer.ReportedCreationTime]: string;
} => ({
  [IRetailer.ReportedCreationTime.OPENING_SOON]: strictLocalize({
    defaultMessage: "Opening soon",
  }),
  [IRetailer.ReportedCreationTime.STARTED_THIS_YEAR]: `${getYear(new Date())}`,
  [IRetailer.ReportedCreationTime.STARTED_ONE_YEAR_AGO]: `${getYear(
    subYears(new Date(), 1)
  )}`,
  [IRetailer.ReportedCreationTime.STARTED_BETWEEN_5_AND_2_YEAR_AGO]:
    strictLocalize(
      {
        defaultMessage: "{fiveYearsAgoNumber}-{twoYearsAgoNumber}",
        description: {
          text: "Label for the started between 2-5 years ago option",
          img: "https://cdn.faire.com/fastly/7ae7f6ddc5cbf8def6523d64f2fbc0dc18d7afa4579167c0a114eca3c7294973.png",
        },
      },
      {
        fiveYearsAgoNumber: getYear(subYears(new Date(), 5)),
        twoYearsAgoNumber: getYear(subYears(new Date(), 2)),
      }
    ),
  [IRetailer.ReportedCreationTime.BEFORE_5_YEAR_AGO]: strictLocalize(
    {
      defaultMessage: "Before {fiveYearsAgoNumber}",
      description: {
        text: "Label for the started between 5 years ago option",
        img: "https://cdn.faire.com/fastly/7ae7f6ddc5cbf8def6523d64f2fbc0dc18d7afa4579167c0a114eca3c7294973.png",
      },
    },
    {
      fiveYearsAgoNumber: getYear(subYears(new Date(), 5)),
    }
  ),
  // deprecated and should never be used, only added to obey types
  [IRetailer.ReportedCreationTime.BETWEEN_5_AND_1_YEAR_AGO]: strictLocalize(
    {
      defaultMessage: "Started in {firstYear}-{secondYear}",
      description: {
        text: "Label for an option where the creation time was between 5 and 1 years ago",
      },
    },
    DEPRECATED_backwardsCompatibleValues({
      firstYear: getYear(subYears(new Date(), 5)),
      secondYear: getYear(subYears(new Date(), 1)),
    })
  ),
  [IRetailer.ReportedCreationTime.STARTED_TWO_YEARS_AGO]: `${getYear(
    subYears(new Date(), 2)
  )}`,
  [IRetailer.ReportedCreationTime.STARTED_THREE_YEARS_AGO]: `${getYear(
    subYears(new Date(), 3)
  )}`,
  [IRetailer.ReportedCreationTime.STARTED_FOUR_YEARS_AGO]: `${getYear(
    subYears(new Date(), 4)
  )}`,
  [IRetailer.ReportedCreationTime.STARTED_FIVE_YEARS_AGO]: `${getYear(
    subYears(new Date(), 5)
  )}`,
  [IRetailer.ReportedCreationTime.STARTED_BEFORE_FOUR_YEARS_AGO]: "",
  [IRetailer.ReportedCreationTime.STARTED_IN_2018]: "",
  [IRetailer.ReportedCreationTime.BETWEEN_2013_AND_2017]: "",
  [IRetailer.ReportedCreationTime.BEFORE_2013]: "",
});

const OPTIONS = [
  IRetailer.ReportedCreationTime.OPENING_SOON,
  IRetailer.ReportedCreationTime.STARTED_THIS_YEAR,
  IRetailer.ReportedCreationTime.STARTED_ONE_YEAR_AGO,
  IRetailer.ReportedCreationTime.STARTED_TWO_YEARS_AGO,
  IRetailer.ReportedCreationTime.STARTED_THREE_YEARS_AGO,
  IRetailer.ReportedCreationTime.STARTED_FOUR_YEARS_AGO,
  IRetailer.ReportedCreationTime.STARTED_FIVE_YEARS_AGO,
  IRetailer.ReportedCreationTime.BEFORE_5_YEAR_AGO,
];

export const CreationYearOptions = (
  strictLocalize: StrictLocalizeFunction,
  isSignup?: boolean
) => {
  const openingYearsOptions = isSignup
    ? OPTIONS
    : getSettingOwFOpeningYearOptions();
  const creationTimeLabel = CREATION_TIME_LABEL(strictLocalize);
  return openingYearsOptions
    .map((creationTime) => {
      if (creationTime in creationTimeLabel) {
        return {
          value: creationTime,
          label: creationTimeLabel[creationTime],
        };
      }

      return null;
    })
    .filter(isNotNull);
};
